import type { StepKey } from './schema';

/**
 * The canonical onboarding step order. Single source of truth for BOTH
 * client (client/src/pages/onboarding.tsx) and server
 * (server/services/onboardingService.ts) — they previously kept two hand-synced
 * copies with a "MUST MATCH BACKEND" comment, which is exactly the kind of
 * duplication that drifts.
 *
 * Order: the number first (it is what the customer came for, often picked on
 * the marketing site before they had an account), then who they are and what
 * they will do with it, then the $1 card check, then the SMS code.
 *
 * The questions sit in front of the card by product decision (2026-09-24):
 * the card screen and the risk review need to know whether this is a cold
 * caller before a trial is handed out. They were previously after the card
 * because a long pre-payment form lost ~40% of signups; they are now one tap
 * per screen (shared/signupQuestionnaire.ts) instead of a form, which is the
 * mitigation. If the funnel drops at use_type again, look here first.
 *
 * phone_verification stays AFTER the card on purpose: an OTP costs real money
 * per send, and putting it behind the $1 check keeps SMS-pumping bots from
 * triggering sends without ever passing a card.
 *
 * `plan_selection` is deliberately NOT here. A new application is born on
 * DEFAULT_SIGNUP_PLAN_ID (see below) and the card screen states the plan,
 * the price and the date in full before anything is handed over. The step
 * and its component still exist: putting it back is one line.
 *
 * `StepKey` is imported as a TYPE ONLY. shared/schema.ts pulls in the whole
 * Drizzle table graph, and this module is imported by lightweight unit tests
 * and by the client bundle — a value import would drag all of it along.
 */
export const ONBOARDING_STEP_ORDER: StepKey[] = [
  'number_selection',
  'use_type',
  'business_profile',
  'payment_collection',
  'phone_verification',
  'trial_activation',
  'password_setup',
];

/**
 * The plan a brand-new application starts on, since the flow no longer asks.
 *
 * OWNER DECISION 2026-10-25: Lite ANNUAL. When the trial ends the card is
 * charged the annual fee once ($90/year at the time of writing), matching the
 * public order page, which sells Lite annually only.
 *
 * ⚠ `starter_yearly` IS Lite. The yearly ids come from an older lineup and are
 * named one tier off (lib/planIntervals) — verify by NAME and amount, never by
 * the id. Read 2026-10-25: name 'Lite', base_price 90.00, billing_interval
 * 'yearly', its own Stripe price.
 *
 * Only NEW applications get it. The plan is stored on each application at
 * creation (onboardingService.createApplication), and the Stripe trial is
 * created on that row's price at the card step, so trials that were shown
 * $9/month keep $9/month. The card screen states the plan, the price, the
 * date and the money-back terms before the card is entered.
 */
export const DEFAULT_SIGNUP_PLAN_ID = 'starter_yearly';
export const DEFAULT_SIGNUP_BILLING_INTERVAL = 'yearly';

/**
 * Each step's single prerequisite. The chain is strictly linear — a step is
 * locked until its predecessor completes. Kept as an explicit map rather than
 * "the previous array element" so an out-of-order insertion is a deliberate
 * edit rather than a silent side effect of reordering the array.
 */
export const STEP_PREREQUISITE: Record<StepKey, StepKey | null> = {
  number_selection: null,
  use_type: 'number_selection',
  business_profile: 'use_type',
  payment_collection: 'business_profile',
  phone_verification: 'payment_collection',
  trial_activation: 'phone_verification',
  password_setup: 'trial_activation',
  // Not in ONBOARDING_STEP_ORDER, so computeLocks never reads this entry —
  // the key stays only because StepKey still contains it.
  plan_selection: null,
};

export function computeLocks(
  completed: Record<StepKey, { completed: boolean }>,
): Record<StepKey, boolean> {
  const locked = {} as Record<StepKey, boolean>;
  for (const step of ONBOARDING_STEP_ORDER) {
    const prereq = STEP_PREREQUISITE[step];
    locked[step] = prereq ? !completed[prereq]?.completed : false;
  }
  return locked;
}

/**
 * The steps that must be complete before the trial can activate.
 *
 * Excludes `trial_activation` itself, and `password_setup` because that runs
 * AFTER activation — anything that force-completes "all steps" at activation
 * time must not tick the password step off, or the user would skip the only
 * screen that collects it.
 *
 * Several places used to hardcode this list (the org lifecycle transition
 * check, the step-row migration, and the activate-trial backfill loop). They
 * all read it from here now; a stale copy silently breaks the transition,
 * because a step that no longer exists can never be marked complete.
 */
export const STEPS_BEFORE_TRIAL: StepKey[] = ONBOARDING_STEP_ORDER.filter(
  (s) => s !== 'trial_activation' && s !== 'password_setup',
);

/** Trial length in days. Server helper lives in server/lib/trialConfig.ts. */
export const TRIAL_DAYS = 7;
